<script lang="ts">
/** Teleport target for dock panel body content (history, goals, etc.). */
export const STATUS_BAR_PANEL_CONTENT_ID = "zw-status-bar-panel-content";

/**
 * Left → right order of status chips.
 * To add a chip: append an id here, gate it in `visibleIds`, then render it below.
 */
export const STATUS_ORDER = [
  "chapter",
  "words",
  "reading",
  "goals",
  "zoom",
  "comments",
  "history",
] as const;

export type StatusItemId = (typeof STATUS_ORDER)[number];
</script>

<script setup lang="ts">
import { computed, onUnmounted, ref, useTemplateRef, watch } from "vue";
import { storeToRefs } from "pinia";
import { useChapterStatsFromSession } from "../composables/useChapterStats";
import { useUiZoom } from "../composables/useUiZoom";
import { isTryHost } from "../lib/hostGate";
import { isJust, just, matchMaybe, nothing, type Maybe } from "../lib/maybe";
import {
  closeSentryFeedback,
  onSentryFeedbackClosed,
  openSentryFeedback,
} from "../lib/sentryFeedback";
import { useEditorChromeStore } from "../stores/useEditorChromeStore";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import { useProjectGoalsStore } from "../stores/useProjectGoalsStore";
import EditorStatusBarPanel from "./EditorStatusBarPanel.vue";
import WritingGoalsPanel from "./WritingGoalsPanel.vue";

const session = useEditorSessionStore();
const goalsStore = useProjectGoalsStore();
const chrome = useEditorChromeStore();
const { label: zoomLabel, resetZoom } = useUiZoom();
const { chapterLabel, wordLabel, readingLabel, canShowHistory } =
  useChapterStatsFromSession();

const {
  statusBarPrefsEffective: prefs,
  goalsOpen,
  historyOpen,
  commentsOpen,
  mobileShell: showCommentsToggle,
  trashPreview,
  outlineActiveFile,
  activeNote,
  activeDatabase,
  editorBottomInset,
} = storeToRefs(session);

const primaryGoalChip = computed(() => goalsStore.primaryGoalChip);
const primaryGoalMet = computed(() =>
  matchMaybe(primaryGoalChip.value, {
    just: (chip) => chip.met,
    nothing: () => false,
  }),
);
const primaryGoalTitle = computed(() =>
  matchMaybe(primaryGoalChip.value, {
    just: (chip) => chip.title,
    nothing: () => "",
  }),
);
const primaryGoalLabel = computed(() =>
  matchMaybe(primaryGoalChip.value, {
    just: (chip) => chip.label,
    nothing: () => "",
  }),
);
/** Sentry feedback form open (styled as the status dock). */
const feedbackOpen = ref(false);

const panelOpen = computed(() => goalsOpen.value || historyOpen.value);
const panelTitle = computed(() =>
  goalsOpen.value ? "Writing goals" : "Change history",
);
const panelSubtitle = computed((): Maybe<string> => {
  if (historyOpen.value === false) return nothing;
  if (isJust(trashPreview.value)) return just(trashPreview.value.value.label);
  if (isJust(outlineActiveFile.value)) return outlineActiveFile.value;
  if (isJust(activeNote.value)) return activeNote.value;
  if (isJust(activeDatabase.value)) return activeDatabase.value;
  return nothing;
});

const dockEl = useTemplateRef<HTMLElement>("dock");

watch(
  dockEl,
  (el, _prev, onCleanup) => {
    if (el === null) {
      editorBottomInset.value = 0;
      document.documentElement.style.removeProperty("--zw-status-dock-height");
      return;
    }
    const report = (): void => {
      const height = Math.ceil(el.getBoundingClientRect().height);
      editorBottomInset.value = height;
      document.documentElement.style.setProperty(
        "--zw-status-dock-height",
        `${height}px`,
      );
    };
    const observer = new ResizeObserver(report);
    observer.observe(el);
    report();
    onCleanup(() => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--zw-status-dock-height");
    });
  },
  { flush: "post" },
);

onSentryFeedbackClosed(
  just(() => {
    feedbackOpen.value = false;
  }),
);
onUnmounted(() => {
  onSentryFeedbackClosed(nothing);
  if (feedbackOpen.value) closeSentryFeedback();
});

watch(historyOpen, (open) => {
  if (open && feedbackOpen.value) closeSentryFeedback();
});

watch(goalsOpen, (open) => {
  if (open && feedbackOpen.value) closeSentryFeedback();
});

const showChapter = computed(() => isJust(chapterLabel.value));
const showWords = computed(() => prefs.value.wordCount);
const showReading = computed(() => prefs.value.readingTime);
const showGoals = computed(
  () =>
    prefs.value.goals === true &&
    matchMaybe(primaryGoalChip.value, {
      just: (chip) => chip.label.length > 0,
      nothing: () => false,
    }),
);
const showZoom = computed(() => prefs.value.zoom);
/** Prod builds only; hidden on the public try.* demo. */
const showFeedback = import.meta.env.PROD === true && isTryHost() === false;

const chapterName = computed(() =>
  matchMaybe(chapterLabel.value, {
    just: (name) => name,
    nothing: () => "",
  }),
);

const visibleIds = computed((): StatusItemId[] =>
  STATUS_ORDER.filter((id) => {
    if (id === "chapter") return showChapter.value === true;
    if (id === "words") return showWords.value === true;
    // The narrow layout drops lower-priority chips so the bar fits the width.
    if (id === "reading")
      return showReading.value === true && showCommentsToggle.value === false;
    if (id === "goals") return showGoals.value === true;
    if (id === "comments") return showCommentsToggle.value === true;
    if (id === "history") return canShowHistory.value === true;
    if (id === "zoom")
      return showZoom.value === true && showCommentsToggle.value === false;
    return false;
  }),
);

const visible = computed(() => visibleIds.value.length > 0 || showFeedback);

function onGoalsClick(event: MouseEvent): void {
  event.preventDefault();
  event.stopPropagation();
  goalsOpen.value = goalsOpen.value === false;
  if (goalsOpen.value) {
    chrome.closeHistory();
    if (feedbackOpen.value) closeSentryFeedback();
  }
}

function onClosePanel(): void {
  if (goalsOpen.value) {
    goalsOpen.value = false;
    return;
  }
  chrome.closeHistory();
}

function onToggleHistory(): void {
  if (historyOpen.value === false && feedbackOpen.value === true) {
    closeSentryFeedback();
  }
  chrome.toggleHistory();
}

function onToggleComments(): void {
  chrome.toggleComments();
}

async function onToggleFeedback(): Promise<void> {
  if (feedbackOpen.value) {
    closeSentryFeedback();
    return;
  }
  goalsOpen.value = false;
  chrome.closeHistory();
  feedbackOpen.value = true;
  const opened = await openSentryFeedback();
  if (opened === false) feedbackOpen.value = false;
}
</script>

<template>
  <div v-if="visible" ref="dock" class="zw-editor-status-dock">
    <EditorStatusBarPanel
      :open="panelOpen"
      :title="panelTitle"
      :subtitle="panelSubtitle"
      @close="onClosePanel"
    >
      <div :id="STATUS_BAR_PANEL_CONTENT_ID" class="contents" />
    </EditorStatusBarPanel>
    <Teleport defer :to="`#${STATUS_BAR_PANEL_CONTENT_ID}`">
      <WritingGoalsPanel v-if="showGoals && goalsOpen" @close="onClosePanel" />
    </Teleport>
    <div class="zw-editor-status" aria-live="polite">
      <slot name="before" />
      <span
        v-if="typeof $slots.before === 'function' && visibleIds.length > 0"
        class="zw-editor-status-sep"
        aria-hidden="true"
        >·</span
      >
      <template v-for="(id, index) in visibleIds" :key="id">
        <span v-if="index > 0" class="zw-editor-status-sep" aria-hidden="true"
          >·</span
        >

        <span
          v-if="id === 'chapter'"
          class="zw-editor-status-metric zw-editor-status-chapter"
          :title="chapterName"
        >
          {{ chapterName }}
        </span>

        <span v-else-if="id === 'words'" class="zw-editor-status-metric">
          {{ wordLabel }}
        </span>

        <span v-else-if="id === 'reading'" class="zw-editor-status-metric">
          {{ readingLabel }}
        </span>

        <button
          v-else-if="id === 'goals'"
          type="button"
          class="zw-editor-status-goals"
          :class="{
            'zw-editor-status-goals-met': primaryGoalMet,
            'zw-editor-status-goals-open': goalsOpen,
          }"
          :title="
            goalsOpen
              ? 'Hide writing goals'
              : primaryGoalTitle.length > 0
                ? primaryGoalTitle
                : 'Writing goals'
          "
          :aria-label="
            goalsOpen
              ? 'Hide writing goals'
              : primaryGoalTitle.length > 0
                ? primaryGoalTitle
                : primaryGoalLabel.length > 0
                  ? primaryGoalLabel
                  : 'Writing goals'
          "
          :aria-pressed="goalsOpen"
          @click="onGoalsClick"
        >
          {{ primaryGoalLabel }}
        </button>

        <button
          v-else-if="id === 'comments'"
          type="button"
          class="zw-editor-status-comments"
          :class="{ 'zw-editor-status-comments-open': commentsOpen }"
          :title="commentsOpen ? 'Hide comments' : 'Show comments'"
          :aria-label="commentsOpen ? 'Hide comments' : 'Show comments'"
          :aria-pressed="commentsOpen"
          @click="onToggleComments"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M7.5 8.25h9m-9 3H12m-6.75 6.75 2.122-2.122a1.5 1.5 0 0 1 1.06-.44H18A2.25 2.25 0 0 0 20.25 13.5v-6A2.25 2.25 0 0 0 18 5.25H6A2.25 2.25 0 0 0 3.75 7.5v9.75Z"
            />
          </svg>
        </button>

        <button
          v-else-if="id === 'history'"
          type="button"
          class="zw-editor-status-history"
          :class="{ 'zw-editor-status-history-open': historyOpen }"
          :title="historyOpen ? 'Hide change history' : 'Change history'"
          :aria-label="historyOpen ? 'Hide change history' : 'Change history'"
          :aria-pressed="historyOpen"
          @click="onToggleHistory"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
            />
          </svg>
        </button>

        <button
          v-else-if="id === 'zoom'"
          type="button"
          class="zw-editor-status-zoom"
          :title="`Interface zoom ${zoomLabel}. ⌘/Ctrl + − 0`"
          :aria-label="`Interface zoom ${zoomLabel}. Click to reset`"
          @click="resetZoom"
        >
          {{ zoomLabel }}
        </button>
      </template>
      <template v-if="showFeedback">
        <span
          v-if="visibleIds.length > 0"
          class="zw-editor-status-sep"
          aria-hidden="true"
          >·</span
        >
        <button
          type="button"
          class="zw-editor-status-feedback"
          :class="{ 'zw-editor-status-feedback-open': feedbackOpen }"
          title="Send feedback"
          aria-label="Send feedback"
          :aria-pressed="feedbackOpen"
          @click="onToggleFeedback"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 4.5M3 15V4.5"
            />
          </svg>
        </button>
      </template>
      <span
        v-if="
          typeof $slots.after === 'function' &&
          (visibleIds.length > 0 || showFeedback)
        "
        class="zw-editor-status-sep"
        aria-hidden="true"
        >·</span
      >
      <slot name="after" />
    </div>
  </div>
</template>

<style scoped>
.zw-editor-status-dock {
  position: absolute;
  z-index: 30;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.zw-editor-status {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  width: max-content;
  max-width: 100%;
  padding: 0.28rem 0.55rem;
  border: 1px solid var(--line);
  border-right: none;
  border-bottom: none;
  border-radius: 0.65rem 0 0 0;
  background: var(--bg-raised);
  font-family: var(--font-ui);
  font-size: 0.8rem;
  letter-spacing: 0.01em;
  color: var(--text-muted);
}

.zw-editor-status-metric {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.zw-editor-status-chapter {
  max-width: 10rem;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--text);
}

.zw-editor-status-sep {
  opacity: 0.55;
}

.zw-editor-status-goals {
  appearance: none;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  cursor: pointer;
  border-radius: 0.2rem;
}

.zw-editor-status-goals:hover {
  color: var(--text-strong);
}

.zw-editor-status-goals-met,
.zw-editor-status-goals-open {
  color: var(--text-strong);
}

.zw-editor-status-goals:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 2px;
}

.zw-editor-status-zoom {
  appearance: none;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  cursor: pointer;
  border-radius: 0.2rem;
}

.zw-editor-status-zoom:hover {
  color: var(--text-strong);
}

.zw-editor-status-zoom:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 2px;
}

.zw-editor-status-history,
.zw-editor-status-comments,
.zw-editor-status-feedback {
  appearance: none;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 0.95rem;
  height: 0.95rem;
  flex-shrink: 0;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: 0.2rem;
}

.zw-editor-status-history svg,
.zw-editor-status-comments svg,
.zw-editor-status-feedback svg {
  width: 0.9rem;
  height: 0.9rem;
}

.zw-editor-status-history:hover,
.zw-editor-status-history-open,
.zw-editor-status-comments:hover,
.zw-editor-status-comments-open,
.zw-editor-status-feedback:hover,
.zw-editor-status-feedback-open {
  color: var(--color-zw-amber);
}

.zw-editor-status-history:focus-visible,
.zw-editor-status-comments:focus-visible,
.zw-editor-status-feedback:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 2px;
}
</style>
